import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { BookOpen, Save, RotateCcw } from 'lucide-react';
import { API } from '@/App';

/**
 * The drafter has no memory of yesterday's corrections. This is that memory: standing
 * instructions sent with every conversion, in his own words, editable when he catches it
 * doing something again.
 */
export const DrafterHouseRules = () => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState('');
  const [fallback, setFallback] = useState('');
  const [autopublish, setAutopublish] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const publishReading = async () => {
    setPublishing(true);
    try {
      const r = await axios.post(`${API}/admin/worksheets/publish-reading-only`, {}, { headers });
      toast.success(r.data.published
        ? `${r.data.published} reading worksheet${r.data.published === 1 ? '' : 's'} published`
        : 'Nothing was waiting');
      window.dispatchEvent(new CustomEvent('admin-data-changed'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That did not work');
    } finally { setPublishing(false); }
  };

  useEffect(() => {
    axios.get(`${API}/admin/worksheets/drafter-settings`, { headers })
      .then((r) => {
        setRules(r.data.house_rules || '');
        setFallback(r.data.default_house_rules || '');
        setAutopublish(!!r.data.autopublish);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/worksheets/drafter-settings`,
        { house_rules: rules, autopublish, ...patch }, { headers });
      setRules(r.data.house_rules);
      setAutopublish(r.data.autopublish);
      toast.success('The drafter has been told');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That did not save');
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-cyan-500/25 bg-cyan-500/[0.04] p-4 mb-5"
      data-testid="drafter-house-rules">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left"
          data-testid="house-rules-toggle">
          <BookOpen className="w-4 h-4 text-cyan-400" />
          <span className="font-semibold text-gray-200">House rules for the AI drafter</span>
          <span className="text-xs text-gray-500">
            {open ? 'hide' : 'sent with every conversion'}
          </span>
        </button>
        <label className="flex items-center gap-2 text-xs text-gray-300">
          <Switch checked={autopublish} onCheckedChange={(v) => { setAutopublish(v); save({ autopublish: v }); }}
            data-testid="drafter-autopublish-toggle" />
          Let a clean conversion publish itself
        </label>
      </div>

      <div className="mt-3 flex items-center gap-3 flex-wrap border-t border-white/5 pt-3">
        <Button size="sm" variant="outline" onClick={publishReading} disabled={publishing}
          className="text-gray-100 border-white/20 hover:bg-white/10 hover:text-white"
          data-testid="publish-reading-only-btn">
          {publishing ? 'Publishing…' : 'Publish the reading-only ones'}
        </Button>
        <span className="text-xs text-gray-500">
          Informational supplements with nothing to fill in — there is no answer to get wrong,
          and they then read on the platform like the exercises do.
        </span>
      </div>

      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-gray-400">
            It remembers nothing between documents, so anything you keep having to fix by hand
            belongs here. One instruction per line.
          </p>
          <Label className="sr-only" htmlFor="house-rules">House rules</Label>
          <textarea
            id="house-rules"
            rows={8}
            value={rules}
            onChange={(e) => setRules(e.target.value)}
            className="w-full rounded-md bg-black/30 border border-white/10 p-3 text-sm
                       text-gray-100 font-mono leading-relaxed"
            data-testid="house-rules-input"
          />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => save({})} disabled={saving}
              className="bg-cyan-600 hover:bg-cyan-700" data-testid="house-rules-save">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save rules'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRules(fallback)}
              className="text-gray-100 border-white/20 hover:bg-white/10 hover:text-white"
              data-testid="house-rules-reset">
              <RotateCcw className="w-4 h-4 mr-1" /> Back to the defaults
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrafterHouseRules;
